<template>
  <ModalBase :title="t('general__data__file-upload-v2')" :open="isOpen" @close="closeModal">
    <template v-if="showAdvancedView" #controls>
      <BadgeSquare badge-text="Advanced" size="small" />
    </template>
    <template #content>
      <div class="container">
        <div class="content">
          <i18n-t
            keypath="general__text__file-upload-description"
            tag="p"
            class="content__description"
          >
            <template #name>
              <span class="content__description__name">{{ props.currentDirectory.name }}</span>
            </template>
          </i18n-t>
          <TextBase :text="t('general__text__file-move-later')" type="label" />
          <div v-if="!showInputFileDrop" class="content__disabled-drop-zone">
            <PulseLoader :loading="true" class="spinner" color="#1d234f" />
            <p class="content__disabled-drop-zone__error-text">Message</p>
          </div>
          <InputFileDrop
            v-if="showInputFileDrop"
            class="content__drop-zone"
            tabindex="0"
            :disabled="isFileInputDisabled"
            @files="onInputChange"
            @items="handleFilesDropped"
          />
          <BreadcrumbFiles
            v-if="showFileView && showAdvancedView"
            :breadcrumbs="fileManager.breadcrumbs.value"
            @onClicked="onBreadcrumbClick"
          />
          <DirectoryListView
            v-if="showFileView && showAdvancedView"
            :directoryItems="fileManager.displayedItems.value"
            :actions="[
              {
                actionName: 'delete',
                icon: 'IconTrash2Medium',
                labelKey: 'general__text__delete',
                appliesOn: ['directory', 'file', 'link']
              }
            ]"
            :disabled-types="['link']"
            @doubleClick="fileManager.openItem"
            @action="handleAction"
          />
          <ul v-if="showFileView && !showAdvancedView" class="content__file-list">
            <template v-for="item of tree.tree.value.children" :key="item.id">
              <li
                class="content__file-list__row"
                :class="{ 'content__file-list__row__error': item.status === false }"
                :file="item"
              >
                <IconFileSmall v-if="item.type === 'file'" class="content__file-list__row__icon" />
                <IconFolderSmall
                  v-else-if="item.type === 'directory'"
                  class="content__file-list__row__icon"
                />
                <span class="content__file-list__row__text">{{ item.name }}</span>
                <span class="content__file-list__row__status">
                  <div
                    v-if="item.status === 'loading'"
                    class="content__file-list__row__status__loading-spinner"
                  >
                    <PulseLoader color="#3f46d6" size="8px" />
                  </div>
                  <IconCheck
                    v-if="item.status === true"
                    class="content__file-list__row__status__icon-success"
                  />
                  <ButtonText
                    v-if="item.status === null"
                    :iconLeft="iconXSmall"
                    size="extra-small-icon"
                    @onClicked="removeItem(item)"
                  ></ButtonText>
                  <button
                    v-if="item.status === false"
                    class="content__file-list__row__status__button"
                    aria-label="Remove failed file"
                    @click="removeItem(item)"
                  >
                    <IconX class="content__file-list__row__status__button__icon-error" />
                  </button>
                </span>
              </li>
            </template>
          </ul>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="footer">
        <template v-if="state === 'selecting' && tree.tree.value.children.length > 0">
          <ButtonOutline
            class="footer__button"
            :buttonText="t('general__text__cancel')"
            :disabled="isUploadingFiles"
            @onClicked="closeModal()"
          />
          <ButtonPrimary
            class="footer__button"
            :buttonText="t('general__text__upload')"
            :disabled="isUploadingFiles"
            @onClicked="uploadFiles()"
          />
        </template>
        <template v-else-if="state === 'confirming'">
          <ButtonPrimary
            class="footer__button"
            :buttonText="t('general__text__done')"
            :disabled="isUploadingFiles"
            @onClicked="submitModal()"
          />
        </template>
      </div>
    </template>
  </ModalBase>
  <FileConflictModal
    v-for="file of conflictFiles"
    :key="file.item.id"
    :open="true"
    :disable-submit="false"
    :name="file.item.name"
    :type="file.item.type"
    @close="() => removeFile(file)"
    @replaceItem="() => replaceFile(file)"
    @saveCopy="() => saveCopy(file)"
  />
</template>
<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import ButtonPrimary from '@/design-system/atoms/buttons/ButtonPrimary.vue'
import IconCheck from '@/design-system/assets/icons/IconCheck.vue'
import IconX from '@/design-system/assets/icons/IconX.vue'
import InputFileDrop from './InputFileDrop.vue'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import useFileList from '@/hooks/attachments/useFileListNew'
import ModalBase from './ModalBase.vue'
import ButtonOutline from '@/design-system/atoms/buttons/ButtonOutline.vue'
import ButtonText from '@/design-system/atoms/buttons/ButtonText.vue'
import IconFileSmall from '@/design-system/assets/icons/IconFileSmall.vue'
import TextBase from '@/design-system/atoms/TextBase.vue'
import { DirectoryItem } from './types'
import IconFolderSmall from '@/design-system/assets/icons/IconFolderSmall.vue'
import DirectoryListView from './list-view/DirectoryListView.vue'
import BreadcrumbFiles from './BreadcrumbFiles.vue'
import BadgeSquare from '@/design-system/atoms/BadgeSquare.vue'
import { postDirectoryItem, replaceDirectoryItem } from './files.service'
import { AxiosError } from 'axios'
import FileConflictModal from './FileConflictModal.vue'
import iconXSmall from '@/design-system/assets/icons/Icon-X-Small.svg'

type Props = {
  currentDirectory: DirectoryItem<'directory'>
  open?: boolean
  fileEntries?: FileSystemEntry[]
}

type State = 'selecting' | 'uploading' | 'confirming'

const props = withDefaults(defineProps<Props>(), {
  open: false,
  fileEntries: () => []
})

type ConflictFile = {
  item: DirectoryItem
  suggestedName: string
}

// emits
interface EMITS {
  (e: 'close'): void
  (e: 'save'): void
}
const emits = defineEmits<EMITS>()

// variables
const { t } = useI18n()
const { tree, addFiles, addEntries, removeItem, fileManager } = useFileList()

const isOpen = ref<boolean>(props.open)
const state = ref<State>('selecting')
const showAdvancedView = ref<boolean>(false)
const conflictFiles = ref<ConflictFile[]>([])

// computed
const isUploadingFiles = computed(() => {
  return tree.tree.value.children.some((file) => file.status === 'loading')
})

const isFileInputDisabled = computed(() => {
  return isUploadingFiles.value || state.value !== 'selecting' || !showInputFileDrop.value
})
const showInputFileDrop = computed(() => {
  return true
})
const showFileView = computed(() => {
  return tree.tree.value.children.length > 0
})

watch(
  () => props.fileEntries,
  (newEntries) => {
    if (newEntries && newEntries.length > 0) {
      addEntries(newEntries)
    }
  },
  { immediate: true }
)

// methods
function closeModal() {
  isOpen.value = false
  emits('close')
}
function onInputChange(files: File[]) {
  addFiles(files)
}

async function uploadRecursively(
  item: DirectoryItem,
  parentDirectoryId: string = props.currentDirectory.id
) {
  try {
    const saved = await postDirectoryItem({
      ...item,
      directoryId: parentDirectoryId
    })

    if (item.type === 'directory' && item.children?.length) {
      await Promise.all(item.children.map((child) => uploadRecursively(child, saved.id)))
    }
    tree.updateNodeDetails(item.id, {
      id: saved.id,
      status: true
    })
  } catch (error) {
    console.error('Error uploading item:', item.name, error)
    const axiosError = error as AxiosError
    if (axiosError.response?.status === 409) {
      const suggestedName =
        (axiosError.response.data as { suggestedName: string }).suggestedName ??
        `${item.name} (copy)`
      conflictFiles.value.push({
        item,
        suggestedName: suggestedName
      })
    } else {
      tree.updateNodeDetails(item.id, {
        status: false
      })
    }
  }
}

async function uploadFiles() {
  const files = tree.tree.value.children
  if (files.length) {
    files.forEach((file) => {
      tree.updateNodeDetails(file.id, {
        status: 'loading'
      })
    })
    await Promise.all(
      files.map((file) => {
        return uploadRecursively(file)
      })
    )
    state.value = 'confirming' // Change state to confirming after upload
  }
}

function submitModal() {
  emits('save')
}

function handleKeyDown(event: KeyboardEvent) {
  if (event.ctrlKey && event.altKey && event.key === 'a') {
    event.preventDefault()
    showAdvancedView.value = !showAdvancedView.value
  } else if (event.key === 'Escape') {
    closeModal()
  }
}

watch(
  () => props.open,
  (newValue) => {
    isOpen.value = newValue
    if (newValue) {
      window.addEventListener('keydown', handleKeyDown)
    } else {
      window.removeEventListener('keydown', handleKeyDown)
      tree.resetTree()
      showAdvancedView.value = false
      state.value = 'selecting'
    }
  },
  { immediate: true }
)

function handleFilesDropped(items: DataTransferItem[]) {
  const entries = items
    .map((item) => item.webkitGetAsEntry())
    .filter((entry) => entry) as FileSystemEntry[]
  addEntries(entries)
}

async function onBreadcrumbClick(option: DirectoryItem) {
  if (!option.id) {
    console.warn('Breadcrumb has no id—skipping navigation', option)
    return
  }
  await fileManager.openItem(option.id)
}

async function handleAction(action: string, items: DirectoryItem[]) {
  fileManager.executeCommand({
    action,
    items
  })
  if (items.length === 0) {
    console.warn('Row action triggered without an item:', action)
    return
  }
}

async function replaceFile({ item }: ConflictFile) {
  try {
    await replaceDirectoryItem(item)
    tree.updateNodeDetails(item.id, {
      status: true
    })
  } catch (e) {
    console.error('Error replacing file:', item.name, e)
    tree.updateNodeDetails(item.id, {
      status: false
    })
    return
  }
}

async function saveCopy({ item, suggestedName }: ConflictFile) {
  try {
    tree.updateNodeDetails(item.id, {
      name: suggestedName,
      status: true
    })
    await postDirectoryItem({
      ...{
        ...item,
        id: undefined
      },
      name: suggestedName,
      directoryId: props.currentDirectory.id
    })
    tree.updateNodeDetails(item.id, {
      status: true
    })
  } catch (e) {
    console.error('Error saving copy of file:', item.name, e)
    tree.updateNodeDetails(item.id, {
      status: false
    })
    return
  }
}

function removeFile({ item }: ConflictFile) {
  conflictFiles.value = conflictFiles.value.filter((file) => file.item.id !== item.id)
  tree.updateNodeDetails(item.id, {
    status: false
  })
}
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins.scss';
@use '@/design-system/design-tokens/mixins.scss';

.modal-upload-files {
  display: flex;
  justify-content: center;
}
.container {
  background-color: variables.$color-pure-white;
  border-radius: variables.$border-radius-small;
  display: flex;
  flex-direction: column;
  width: 544px;
  .content {
    display: flex;
    flex-direction: column;
    gap: 16px;
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    @include mixins.scrollbar-vertical;
    &__description {
      @include font-mixins.desktop-bold-paragraph;
      color: variables.$color-greyscale-content;
      margin: 0 0 16px 0;
    }

    &__file-list {
      display: block;
      list-style-type: none;
      margin-block-end: 16px;
      margin-block-start: 0;
      max-height: 184px;
      overflow-x: hidden;
      overflow-y: auto;
      padding-inline-end: 0;
      padding-inline-start: 0;
      @include mixins.scrollbar-vertical;
      &__row {
        align-items: center;
        background-color: variables.$color-greyscale-background;
        border-radius: 12px;
        display: flex;
        gap: 8px;
        height: 48px;
        margin-bottom: 8px;
        padding: 0 16px;
        &:last-child {
          margin-bottom: 0;
        }
        &__error {
          background-color: #fff1f0;
        }
        &__icon {
          color: variables.$color-greyscale-content;
          height: 24px;
          min-width: 24px;
        }
        &__status {
          display: flex;
          justify-items: flex-end;
          margin-left: auto;
          &__button {
            all: unset;
            &:focus {
              outline: revert;
            }
            height: 24px;
            width: 24px;
            &__icon-error {
              color: variables.$color-error;
              cursor: pointer;
              height: 24px;
              min-width: 24px;
            }
          }
          &__icon-success {
            color: variables.$color-success;
            height: 24px;
            min-width: 24px;
          }
          &__loading-spinner {
            min-width: 36px;
          }
        }
        &__text {
          @include font-mixins.desktop-bold-paragraph;
          color: variables.$color-greyscale-content;
          display: inline-block;
          overflow: hidden;
          text-overflow: ellipsis;
          vertical-align: top;
          white-space: nowrap;
        }
      }
    }
  }
}
.spinner {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
}
.footer {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 16px;

  &__button {
    width: 100%;
  }
}
@media screen and (max-width: 768px) {
  .container {
    max-width: 80%;
  }
}
</style>
